import React, { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { ConfigProvider, notification, theme as antdTheme } from "antd";
import MenuIcon from "@mui/icons-material/Menu";
import SearchIcon from "@mui/icons-material/Search";
import NotificationsNoneOutlinedIcon from "@mui/icons-material/NotificationsNoneOutlined";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";
import { logout } from "../../app/store/actions/authActions";
import { isExternalFinanceStoreSubscription } from "../../financeTierAccess";
import Logo from "../../app/components/chainz-logo.png";
import {
  FINANCE_ICONS,
  financeGroups,
  financePageMeta,
  isFinanceItemActive,
} from "./financeNav";
import { os } from "./osClient";
import { formatStamp } from "./osRules";
import "./financeOs.css";

const THEME_KEY = "cz-theme";
const SIDE_KEY = "fo-sub-mini";

const NavIcon = ({ Icon, fontSize = 19 }) =>
  Icon ? <Icon sx={{ fontSize }} /> : null;

const NavGroups = ({ groups, pathname, onNavigate }) =>
  groups.map((group) => (
    <div key={group.name}>
      <div className="fo-group-label">
        <span className="fo-lbl">{group.name}</span>
        <span className="fo-dv" />
      </div>
      {group.items.map((item) => {
        const on = isFinanceItemActive(item, pathname);
        return (
          <button
            key={item.key}
            type="button"
            className={`fo-nav-item${on ? " on" : ""}`}
            title={item.label}
            onClick={() => onNavigate(item.path)}
          >
            <span className="fo-nav-tile">
              <NavIcon Icon={FINANCE_ICONS[item.key]} />
            </span>
            <span className="fo-lbl fo-nav-label">{item.label}</span>
          </button>
        );
      })}
    </div>
  ));

const searchTarget = (kind, row) => {
  if (kind === "transaction") {
    return row.vendorId
      ? `/warehousing/finance/transactions?vendorId=${row.vendorId}`
      : "/warehousing/finance/transactions";
  }
  if (kind === "invoice") {
    return `/warehousing/finance/invoice-register?search=${encodeURIComponent(row.invoiceNumber || "")}`;
  }
  return `/warehousing/finance/vendors/${row._id}`;
};

const noticeTarget = (item) => {
  const type = String(item.entityType || item.kind || "").toLowerCase();
  if (type.includes("invoice")) return "/warehousing/finance/invoice-register";
  if (type.includes("compensation") || type.includes("fee")) return "/warehousing/finance/fees-compensations";
  if (type.includes("reconcil") || type.includes("cash")) return "/warehousing/finance/cash-bank";
  if (type.includes("due") || type.includes("reminder")) return "/warehousing/finance/payouts/due-board";
  if (type.includes("payout")) return "/warehousing/finance/payouts/workflow";
  return "/warehousing/finance/audit";
};

export const FinanceShell = ({ user, token, logout }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const meta = financePageMeta(pathname);
  const external = isExternalFinanceStoreSubscription(user);
  const groups = useMemo(() => financeGroups(external), [external]);

  const [theme, setTheme] = useState(() => {
    try {
      return localStorage.getItem(THEME_KEY) === "light" ? "light" : "dark";
    } catch (err) {
      return "dark";
    }
  });
  const [mini, setMini] = useState(() => {
    try {
      return localStorage.getItem(SIDE_KEY) === "1";
    } catch (err) {
      return false;
    }
  });
  const [drawer, setDrawer] = useState(false);
  const [query, setQuery] = useState("");
  const [hits, setHits] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [notesOpen, setNotesOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [notes, setNotes] = useState([]);

  useEffect(() => {
    notification.config({ placement: "bottomRight", top: 24 });
  }, []);

  useEffect(() => {
    document.body.classList.add("fo-mode");
    return () => {
      document.body.classList.remove("fo-mode", "fo-lite");
      window.dispatchEvent(new Event("fo-theme"));
    };
  }, []);

  useEffect(() => {
    document.body.classList.toggle("fo-lite", theme === "light");
    window.dispatchEvent(new Event("fo-theme"));
  }, [theme]);

  useEffect(() => {
    setDrawer(false);
    setQuery("");
    setHits(null);
    setMenuOpen(false);
    setNotesOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (meta.bare || !token) return undefined;
    let live = true;
    const refresh = () => {
      os.unreadCount(token)
        .then((data) => { if (live) setUnread(data?.count || 0); })
        .catch(() => {});
    };
    refresh();
    const timer = setInterval(refresh, 60000);
    return () => {
      live = false;
      clearInterval(timer);
    };
  }, [token, meta.bare, pathname]);

  useEffect(() => {
    if (meta.bare || !token) return undefined;
    const q = query.trim().slice(0, 80);
    if (!q) {
      setHits(null);
      return undefined;
    }
    let live = true;
    const timer = setTimeout(() => {
      os.search(token, q)
        .then((data) => { if (live) setHits(data || {}); })
        .catch(() => { if (live) setHits({ error: true, transactions: [], invoices: [], brands: [] }); });
    }, 300);
    return () => {
      live = false;
      clearTimeout(timer);
    };
  }, [query, token, meta.bare]);

  const toggleTheme = () => {
    const next = theme === "light" ? "dark" : "light";
    setTheme(next);
    try {
      localStorage.setItem(THEME_KEY, next);
    } catch (err) {
      /* ignore private mode */
    }
  };

  const toggleMini = () => {
    setMini((current) => {
      const next = !current;
      try {
        localStorage.setItem(SIDE_KEY, next ? "1" : "0");
      } catch (err) {
        /* ignore private mode */
      }
      return next;
    });
  };

  const go = (path) => {
    setDrawer(false);
    setQuery("");
    navigate(path);
  };

  const openNotes = async () => {
    setMenuOpen(false);
    setNotesOpen((open) => !open);
    if (notesOpen || !token) return;
    try {
      const data = await os.notifications(token, { page: 1, limit: 20 });
      setNotes(data?.notifications?.docs || []);
      setUnread(data?.unreadCount || 0);
    } catch (err) {
      setNotes([]);
    }
  };

  const openNote = async (item) => {
    try {
      if (!item.read) await os.markRead(token, item._id);
    } catch (err) {
      /* toast already shown */
    }
    setUnread((count) => Math.max(0, count - (item.read ? 0 : 1)));
    setNotesOpen(false);
    navigate(external && String(item.entityType || "").includes("invoice") ? "/warehousing/finance/audit" : noticeTarget(item));
  };

  const markAll = async () => {
    try {
      await os.markAllRead(token);
      setUnread(0);
      setNotes((current) => current.map((item) => ({ ...item, read: true })));
    } catch (err) {
      /* toast already shown */
    }
  };

  const displayName = user?.name || user?.fullName || user?.username || user?.email || "Finance";
  const initials = String(displayName)
    .split(/[\s@._-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("") || "F";

  if (meta.bare) {
    return (
      <div className={`fo-app cz-root fo-auth${theme === "light" ? " lite" : ""}`}>
        <Outlet />
      </div>
    );
  }

  return (
    <div className={`fo-app cz-root fo-root${theme === "light" ? " lite" : ""}`}>
      <aside className="fo-desk">
        <button type="button" className="fo-logo" title="Finance Dashboard" onClick={() => go("/warehousing/finance")}>
          <img src={Logo} alt="Chainz" />
        </button>
        <button type="button" className={`fo-collapse${mini ? "" : " open"}`} onClick={toggleMini} aria-label="Collapse sections">
          <ChevronRightIcon sx={{ fontSize: 18 }} />
        </button>
        <nav className={`fo-sub${mini ? " mini" : ""}`}>
          <NavGroups groups={groups} pathname={pathname} onNavigate={go} />
        </nav>
      </aside>

      <div className="fo-column">
        <div className="fo-top">
          <button type="button" className="fo-icon-btn fo-mob" onClick={() => setDrawer(true)} aria-label="Open menu">
            <MenuIcon sx={{ fontSize: 20 }} />
          </button>
          <div className="fo-search">
            <SearchIcon sx={{ fontSize: 18 }} />
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Escape") setQuery("");
              }}
              placeholder="Search transactions, invoices, brands..."
              aria-label="Search transactions, invoices, and brands"
            />
            {query.trim() && (
              <div className="fo-search-menu">
                {hits?.error && <div className="fo-search-empty">Search is unavailable.</div>}
                <SearchGroup label="Transactions" rows={hits?.transactions} empty={hits == null} onOpen={(row) => go(searchTarget("transaction", row))} render={(row) => `${row.type || "Transaction"} · ${row.brandName || ""}`} />
                {!external && (
                  <SearchGroup label="Invoices" rows={hits?.invoices} empty={hits == null} onOpen={(row) => go(searchTarget("invoice", row))} render={(row) => `${row.invoiceNumber} · ${row.brandName || ""}`} />
                )}
                <SearchGroup label="Brands" rows={hits?.brands} empty={hits == null} onOpen={(row) => go(searchTarget("brand", row))} render={(row) => row.brandName || row.name} />
              </div>
            )}
          </div>
          <div className="fo-top-actions">
            <div className="fo-user">
              <button type="button" className="fo-icon-btn" onClick={openNotes} aria-label="Notifications">
                <NotificationsNoneOutlinedIcon sx={{ fontSize: 20 }} />
                {unread > 0 && <span className="fo-count-dot">{unread > 9 ? "9+" : unread}</span>}
              </button>
              {notesOpen && (
                <div className="fo-user-menu fo-note-menu">
                  <div className="fo-note-head">
                    <strong>Notifications</strong>
                    <button type="button" className="fo-btn" onClick={markAll}>Mark all read</button>
                  </div>
                  {notes.length === 0 && <div className="fo-search-empty">No notifications.</div>}
                  {notes.map((item) => (
                    <button key={item._id} type="button" className={`fo-search-hit${item.read ? "" : " unread"}`} onClick={() => openNote(item)}>
                      <span>
                        <strong>{item.title}</strong>
                        <em>{formatStamp(item.createdAt)}</em>
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button type="button" className="fo-icon-btn" onClick={toggleTheme} aria-label="Toggle theme">
              {theme === "light" ? (
                <LightModeOutlinedIcon sx={{ fontSize: 20 }} />
              ) : (
                <DarkModeOutlinedIcon sx={{ fontSize: 20 }} />
              )}
            </button>
            <div className="fo-user">
              <button type="button" className="fo-user-btn" onClick={() => setMenuOpen((open) => !open)}>
                <span className="fo-avatar">{initials}</span>
                <span className="fo-uname">
                  <span className="fo-user-name">{displayName}</span>
                  <span className="fo-user-role">Finance</span>
                </span>
              </button>
              {menuOpen && (
                <div className="fo-user-menu">
                  <button
                    type="button"
                    onClick={() => {
                      navigate("/warehousing/finance/login");
                      logout();
                    }}
                  >
                    Log out
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="fo-pagehead">
          <div>
            <div className="fo-kicker">Finance OS · {meta.group}</div>
            <h1 className="fo-h1">{meta.title}</h1>
          </div>
          {meta.primary && (
            <button
              type="button"
              className="fo-btn fo-btn-primary"
              onClick={() => {
                if (meta.primary.event) {
                  window.dispatchEvent(new Event(meta.primary.event));
                  return;
                }
                go(meta.primary.to);
              }}
            >
              <AddIcon sx={{ fontSize: 16 }} />
              {meta.primary.label}
            </button>
          )}
        </div>

        <div className="fo-page">
          <ConfigProvider
            theme={{
              algorithm: theme === "light" ? antdTheme.defaultAlgorithm : antdTheme.darkAlgorithm,
              token: {
                colorPrimary: "#7D010C",
                borderRadius: 12,
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                colorBgContainer: theme === "light" ? "#fffaf9" : "transparent",
                colorBgElevated: theme === "light" ? "#fffaf9" : "#160b0d",
              },
            }}
          >
            <Outlet />
          </ConfigProvider>
        </div>
      </div>

      {drawer && (
        <>
          <div className="fo-overlay" onClick={() => setDrawer(false)} />
          <div className="fo-drawer">
            <div className="fo-drawer-head">
              <img src={Logo} alt="Chainz" />
              <div>Finance OS</div>
              <button type="button" className="fo-icon-btn" onClick={() => setDrawer(false)} aria-label="Close menu">
                <CloseIcon sx={{ fontSize: 18 }} />
              </button>
            </div>
            <NavGroups groups={groups} pathname={pathname} onNavigate={go} />
          </div>
        </>
      )}
    </div>
  );
};

const SearchGroup = ({ label, rows, empty, onOpen, render }) => (
  <div>
    <div className="fo-search-label">{label}</div>
    {empty && <div className="fo-search-empty">Searching…</div>}
    {!empty && (rows || []).length === 0 && <div className="fo-search-empty">No matches.</div>}
    {(rows || []).map((row) => (
      <button key={row._id} type="button" className="fo-search-hit" onClick={() => onOpen(row)}>
        <span>{render(row)}</span>
      </button>
    ))}
  </div>
);

const mapStateToProps = (state) => ({
  user: state.auth?.user,
  token: state.auth?.token,
});

export default connect(mapStateToProps, { logout })(FinanceShell);
